import React, { useState } from 'react';
import { useAuthStore } from '@passport/store';
import './pages.css';

// Reuses @passport/store's changePassword action directly (not @passport/auth-core's own
// ChangePasswordPage component, which operates on auth-core's separate store — the one this
// whole app ecosystem has moved away from, see ProfilePage.tsx's own history for the same
// @admin/api decoupling reasoning). This keeps the page consistent with every other
// @passport/pages page (ProfilePage included), which all read/act on @passport/store.
export const ChangePasswordPage: React.FC = () => {
  const { changePassword, isLoading, error, clearError } = useAuthStore();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [succeeded, setSucceeded] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    clearError();

    if (newPassword.length < 8) {
      setValidationError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setValidationError('New password and confirmation do not match.');
      return;
    }

    // changePassword() logs the user out on success (invalidates the old session) — see
    // authStore.ts. Show a confirmation instead of immediately bouncing to the sign-in
    // redirect, so the user understands why they're about to be signed out.
    const success = await changePassword(currentPassword, newPassword);
    if (success) {
      setSucceeded(true);
    }
  };

  if (succeeded) {
    return (
      <div className="admin-page-container">
        <div className="page-header">
          <h1 className="page-title">Change Password</h1>
        </div>
        <div className="page-card">
          <div className="page-card-body">
            <div className="page-alert page-alert-success">
              <span className="page-alert-message">
                Password changed successfully. You've been signed out for security — please sign
                in again with your new password.
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <div className="page-header">
        <h1 className="page-title">Change Password</h1>
        <p className="page-subtitle">Update the password for your account</p>
      </div>

      <div className="page-card">
        <div className="page-card-body">
          <form onSubmit={handleSubmit} className="page-form" noValidate>
            {(validationError || error) && (
              <div className="page-alert page-alert-error">
                <span className="page-alert-message">{validationError || error}</span>
              </div>
            )}

            <div className="page-form-group">
              <label htmlFor="current-password" className="page-form-label required">
                Current Password
              </label>
              <input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="page-form-input"
                required
                autoComplete="current-password"
                disabled={isLoading}
              />
            </div>

            <div className="page-form-group">
              <label htmlFor="new-password" className="page-form-label required">
                New Password
              </label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="page-form-input"
                required
                autoComplete="new-password"
                disabled={isLoading}
                placeholder="At least 8 characters"
              />
            </div>

            <div className="page-form-group">
              <label htmlFor="confirm-password" className="page-form-label required">
                Confirm New Password
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="page-form-input"
                required
                autoComplete="new-password"
                disabled={isLoading}
              />
            </div>

            <div className="page-btn-group">
              <button type="submit" className="page-btn page-btn-primary" disabled={isLoading}>
                {isLoading ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
